/**
 * Dynamic block CLI mutations — DYNLIST / DYNSET / DYNRESET.
 *
 * Pure helpers that read / mutate an INSERT's flat
 * `customProperties: Record<string, string | number | boolean>` bag
 * using the master block's typed `dynamicMetadata.parameters` (when
 * present) to validate values and supply defaults. See
 * `packages/drawing-model/src/dxfBridge.dynamic.params.ts` for the DXF
 * import path that originally seeds this bag from the authored
 * parameter defaults.
 *
 * ── Scope ──
 *
 * No geometry re-evaluation happens here — mirrors the 8ab89de commit
 * scope note: "populate INSERT customProperties from dynamic-block
 * eval graph" is storage-only state, not re-running the evaluator. The
 * evaluator consumes customProperties on its next pass (grip edit /
 * re-render) just like today.
 *
 * Covers the 5 parameter kinds the task specifies:
 *   distance (linear / polar distance) → number
 *   angle    (rotation / polar angle)  → number
 *   flip                                → boolean
 *   visibility                          → string (choices from states)
 *   lookup                              → string (choices from lookup table)
 */

import type {
  BlockDefinition,
  BlockInsertEntity,
  DrawingEntity,
  DynamicBlockMetadata,
  DynamicParameter,
} from "@dxf-ai-editor/drawing-model";

import {
  parseAngleValue,
  parseParameterValue,
  parsePointValue,
} from "./parseParameterValue";

// ═══════════════════════════════════════════════════════════════════════════
// Public types
// ═══════════════════════════════════════════════════════════════════════════

export type DynamicParamType =
  | "distance"
  | "angle"
  | "flip"
  | "visibility"
  | "lookup"
  | "unknown";

/**
 * Typed descriptor for a single dynamic parameter surfaced by DYNLIST.
 * `kind: "param"` describes a live row; `kind: "empty"` signals a block
 * with no parameters; `kind: "error"` reports a validation failure
 * (selection is not a Block, etc.).
 */
export type DynListLine =
  | {
      kind: "param";
      name: string;
      type: DynamicParamType;
      value: string | number | boolean;
      defaultValue?: string | number | boolean;
      choices?: readonly string[];
      min?: number;
      max?: number;
    }
  | { kind: "empty"; message: string }
  | { kind: "error"; message: string };

export interface DynSetSuccess {
  ok: true;
  entity: BlockInsertEntity;
  summary: string;
  before: string | number | boolean;
  after: string | number | boolean;
}

export interface DynSetFailure {
  ok: false;
  message: string;
}

export type DynSetResult = DynSetSuccess | DynSetFailure;

export interface DynResetSuccess {
  ok: true;
  entity: BlockInsertEntity;
  count: number;
  summary: string;
}

export interface DynResetFailure {
  ok: false;
  message: string;
}

export type DynResetResult = DynResetSuccess | DynResetFailure;

// ═══════════════════════════════════════════════════════════════════════════
// Parameter metadata helpers (typed view over block.dynamicMetadata)
// ═══════════════════════════════════════════════════════════════════════════

interface ParamInfo {
  name: string;
  type: DynamicParamType;
  defaultValue: string | number | boolean;
  choices?: readonly string[];
  min?: number;
  max?: number;
}

/**
 * Derive a typed ParamInfo from a single `DynamicParameter`. Mirrors the
 * mapping in `extractDynamicBlockParamsFromSections` so the reset /
 * choices validation use the same source of truth. Unknown / complex
 * kinds (point, polar, xy, alignment, constraints, ...) fall through to
 * undefined — the CLI commands handle those rows opportunistically via
 * their current flat customProperties values.
 */
function paramInfoFromDynamicParameter(
  param: DynamicParameter,
  block: BlockDefinition,
): ParamInfo | null {
  switch (param.kind) {
    case "linear":
      return {
        name: param.name,
        type: "distance",
        defaultValue: Math.abs(param.currentDistance ?? param.defaultDistance ?? 0),
        ...(param.valueSet?.min !== undefined
          ? { min: param.valueSet.min }
          : {}),
        ...(param.valueSet?.max !== undefined
          ? { max: param.valueSet.max }
          : {}),
      };
    case "rotation":
      return {
        name: param.name,
        type: "angle",
        defaultValue: roundAngle(param.currentAngle ?? 0),
      };
    case "flip":
      return {
        name: param.name,
        type: "flip",
        defaultValue: param.currentValue ?? false,
      };
    case "visibility": {
      const choices = param.states.map((s) => s.name);
      const current =
        param.states.find((s) => s.id === param.currentStateId) ??
        param.states.find((s) => s.id === param.defaultStateId) ??
        param.states[0];
      return {
        name: param.name,
        type: "visibility",
        defaultValue: current?.name ?? choices[0] ?? "",
        choices,
      };
    }
    case "lookup": {
      const meta = block.dynamicMetadata;
      const table = meta?.lookupTables.find(
        (t) => t.id === param.lookupTableId,
      );
      const choices = table?.rows.map((r) => r.id) ?? [];
      const current =
        param.currentLookupRowId ?? table?.rows[0]?.id ?? choices[0] ?? "";
      return {
        name: param.name,
        type: "lookup",
        defaultValue: current,
        choices,
      };
    }
    default:
      return null;
  }
}

function roundAngle(deg: number): number {
  const rounded = Math.round(deg * 1e6) / 1e6;
  return rounded === 0 ? 0 : rounded;
}

/**
 * Build a (paramName → ParamInfo) map for a block's typed parameters.
 * Empty when the block has no `dynamicMetadata` — callers should fall
 * back to flat customProperties for type inference in that case.
 */
function buildParamInfoMap(
  block: BlockDefinition | undefined,
): Map<string, ParamInfo> {
  const map = new Map<string, ParamInfo>();
  if (!block?.dynamicMetadata) return map;
  for (const p of block.dynamicMetadata.parameters) {
    const info = paramInfoFromDynamicParameter(p, block);
    if (info) map.set(info.name, info);
  }
  return map;
}

/**
 * Infer the DynamicParamType of an existing customProperties value
 * (used as a fallback when the block has no typed dynamicMetadata —
 * i.e. real AutoCAD output where detectDynamicBlocks set the flag but
 * the parser tier didn't decode the typed record). A simple value-based
 * heuristic is enough because the customProperties bag was seeded from
 * `extractDynamicBlockParamsFromSections` with the correct primitive
 * type.
 */
function inferTypeFromValue(
  value: string | number | boolean,
): DynamicParamType {
  if (typeof value === "number") return "distance";
  if (typeof value === "boolean") return "flip";
  return "unknown";
}

// ═══════════════════════════════════════════════════════════════════════════
// INSERT-seed helper — populate customProperties from authored defaults
// ═══════════════════════════════════════════════════════════════════════════
//
// When a fresh INSERT is created (via `geometry/create-block-insert`,
// via `postAction=convert` on block definition, via the AI block
// dispatcher, ...) the entity starts with `customProperties: {}`. For
// dynamic blocks this is a latent bug: DYNSET checks
// `Object.prototype.hasOwnProperty.call(custom, name)` and reports
// "block has no dynamic parameters" when the bag is empty. The
// DXF-import path fixes this via `attachInsertCustomProperties` in
// `dxfBridge.dynamic.params.ts`, but new-insert flows had no
// equivalent. This helper closes the gap so freshly-inserted blocks
// are immediately addressable by DYNSET / property palette / evaluator
// without waiting for a grip edit or DXF round-trip.
//
// The mapping matches the keys consumed by `applyInstanceParameters`
// in `drawing-model/src/dynamicBlockMetadata.ts` (look at the comment
// block at "Customproperties keys we recognise") so round-trips stay
// consistent. Parameter kinds that use multi-component suffixed keys
// (point / polar / xy) seed both axes so the evaluator has a complete
// view. Complex / DXF-only kinds (basepoint — no runtime value; lookup
// where the block has no lookupTables attached yet) are skipped.

/**
 * Seed a fresh `customProperties` map from a block's
 * `dynamicMetadata.parameters`. The resulting map's keys match the
 * scheme consumed by `applyInstanceParameters` and the DYNSET CLI:
 *
 *   linear / rotation / flip / visibility / lookup → `<name>`
 *   alignment / constraint-angular                → `<name>`
 *   constraint-horizontal / -vertical / -aligned  → `<name>`
 *   constraint-radial                              → `<name>`
 *   constraint-diameter                            → `<name>`
 *   point  / xy                                    → `<name>.x`, `<name>.y`
 *   polar                                          → `<name>.distance`, `<name>.angle`
 *
 * Visibility values are stored as the STATE NAME (not the state id) so
 * the DYNSET CLI — which receives the state name as `rawValue` — finds
 * a match under `info.choices` and validates cleanly.
 *
 * Returns an empty object when metadata is undefined or carries no
 * parameters, so callers can unconditionally use `?? {}` at the call
 * site.
 */
export function initCustomPropertiesFromMetadata(
  metadata: DynamicBlockMetadata | undefined,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!metadata || metadata.parameters.length === 0) return out;
  for (const param of metadata.parameters) {
    switch (param.kind) {
      case "linear": {
        // Direction sign is carried by basePoint / secondPoint, not the
        // numeric value — Properties Palette / DYNSET show the absolute
        // distance (matches AutoCAD).
        out[param.name] = Math.abs(param.defaultDistance ?? 0);
        break;
      }
      case "flip": {
        out[param.name] = param.defaultValue ?? false;
        break;
      }
      case "visibility": {
        const def =
          param.states.find((s) => s.id === param.defaultStateId) ??
          param.states[0];
        if (def) out[param.name] = def.name;
        break;
      }
      case "basepoint": {
        // No runtime override — base point is fixed at author time.
        break;
      }
      case "point": {
        // `applyInstanceParameters` reads `<name>.x` / `<name>.y`; seed
        // both so the runtime overlay has a complete view even when the
        // user never edits the parameter.
        out[`${param.name}.x`] = param.currentPoint.x;
        out[`${param.name}.y`] = param.currentPoint.y;
        break;
      }
      case "polar": {
        out[`${param.name}.distance`] = param.defaultDistance ?? 0;
        out[`${param.name}.angle`] = roundAngle(param.defaultAngle ?? 0);
        break;
      }
      case "xy": {
        out[`${param.name}.x`] = param.defaultX ?? 0;
        out[`${param.name}.y`] = param.defaultY ?? 0;
        break;
      }
      case "rotation": {
        out[param.name] = roundAngle(param.defaultAngle ?? 0);
        break;
      }
      case "lookup": {
        // Seed with the first row id so DYNSET has a valid choice to
        // enumerate. When the referenced table is absent (parser
        // tier-skipped), fall back to the authored currentLookupRowId
        // if present; otherwise leave the key absent (static block
        // behaviour).
        const table = metadata.lookupTables.find(
          (t) => t.id === param.lookupTableId,
        );
        const rowId =
          param.currentLookupRowId ?? table?.rows[0]?.id ?? undefined;
        if (rowId !== undefined) out[param.name] = rowId;
        break;
      }
      case "constraint-horizontal":
      case "constraint-vertical":
      case "constraint-aligned": {
        // All three expose a single numeric current distance under
        // `<name>` (see applyInstanceParameters). `<name>.distance` is
        // also accepted but the primary key matches DYNSET's lookup.
        out[param.name] = param.defaultDistance ?? 0;
        break;
      }
      case "constraint-angular": {
        out[param.name] = roundAngle(param.defaultAngle ?? 0);
        break;
      }
      case "alignment": {
        out[param.name] = roundAngle(param.defaultAngle ?? 0);
        break;
      }
      case "constraint-radial": {
        out[param.name] = param.defaultRadius ?? 0;
        break;
      }
      case "constraint-diameter": {
        out[param.name] = param.defaultDiameter ?? 0;
        break;
      }
      case "user": {
        // DYN-4 Fix 3: User parameter seeds a single value under `<name>`.
        // Value type matches the backing ACDBASSOCVARIABLE: numeric for
        // scalar kinds (real / distance / area / angle), string for the
        // `string` kind. DYNSET reads the same key back for editing.
        //
        // P7 round 3: when this user param drives the key column of a
        // BlockPropertiesTable (constraint-based parametric block — e.g.
        // "Двутавр" on the ibeam block), AutoCAD surfaces the first BPT
        // row key as the initial value rather than the raw scalar
        // `defaultValue` (often numeric 0, which is not a valid key). We
        // match that here so the Properties palette / buildBlockInsertPreview
        // see a meaningful stored value from the very first seed — without
        // this, `stored ?? defaultText` short-circuits on the useless "0"
        // seed and the dropdown renders blank. Non-BPT user params keep
        // the existing behaviour (scalar defaultValue verbatim).
        const bpt = metadata.blockPropertiesTable;
        const keyDriverHandle = bpt?.keyColumn?.driverParameterHandle;
        const isKeyDriver =
          keyDriverHandle !== undefined &&
          keyDriverHandle.length > 0 &&
          (keyDriverHandle === param.id ||
            keyDriverHandle === (param.associatedVariableHandle ?? ""));
        if (isKeyDriver && bpt) {
          const firstRowKey = bpt.rows?.[0]?.key;
          if (typeof firstRowKey === "string" && firstRowKey.length > 0) {
            out[param.name] = firstRowKey;
            break;
          }
        }
        out[param.name] = param.defaultValue;
        break;
      }
      default: {
        // Exhaustiveness guard — if a new DynamicParameter kind is
        // added without a case above, TypeScript flags this line.
        const _never: never = param;
        return _never;
      }
    }
  }
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Selection helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Narrow a DrawingEntity to a BlockInsertEntity. Returns null for
 * non-Block entities so the caller can emit a typed error.
 */
export function asBlockInsert(
  entity: DrawingEntity | undefined,
): BlockInsertEntity | null {
  if (!entity || entity.type !== "Block") return null;
  return entity;
}

// ═══════════════════════════════════════════════════════════════════════════
// DYNLIST
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Produce the transcript rows for the selected INSERT. Returns a single
 * error / empty row for degenerate inputs (non-Block, block with no
 * parameters). Rows preserve the `customProperties` insertion order and,
 * when the block has typed metadata, are enriched with default / choices
 * / min / max.
 */
export function listDynamicParameters(
  entity: DrawingEntity | undefined,
  block: BlockDefinition | undefined,
): DynListLine[] {
  const insert = asBlockInsert(entity);
  if (!insert) {
    return [
      {
        kind: "error",
        message: `DYNLIST requires a Block entity; selected ${
          entity?.type ?? "none"
        }.`,
      },
    ];
  }
  const custom = insert.customProperties ?? {};
  const keys = Object.keys(custom);
  if (keys.length === 0) {
    return [
      {
        kind: "empty",
        message: `Block ${insert.id} has no dynamic parameters.`,
      },
    ];
  }
  const infoMap = buildParamInfoMap(block);
  const lines: DynListLine[] = [];
  for (const name of keys) {
    const value = custom[name]!;
    const info = infoMap.get(name);
    const type: DynamicParamType = info?.type ?? inferTypeFromValue(value);
    const line: DynListLine = {
      kind: "param",
      name,
      type,
      value,
      ...(info?.defaultValue !== undefined
        ? { defaultValue: info.defaultValue }
        : {}),
      ...(info?.choices && info.choices.length > 0
        ? { choices: info.choices }
        : {}),
      ...(info?.min !== undefined ? { min: info.min } : {}),
      ...(info?.max !== undefined ? { max: info.max } : {}),
    };
    lines.push(line);
  }
  return lines;
}

/**
 * Format DYNLIST output as a single multi-line transcript string,
 * matching the header + indented-rows shape shown in the task spec.
 * Keeps every row short enough to fit in the audit entry.
 */
export function formatDynListTranscript(
  entity: DrawingEntity | undefined,
  block: BlockDefinition | undefined,
): string {
  const insert = asBlockInsert(entity);
  const lines = listDynamicParameters(entity, block);
  const first = lines[0];
  if (!first) {
    return "DYNLIST: no output.";
  }
  if (first.kind === "error" || first.kind === "empty") {
    return first.message;
  }
  const header =
    insert && block
      ? `Dynamic block ${insert.blockName} (master "${block.name}"):`
      : insert
        ? `Dynamic block ${insert.blockName}:`
        : "Dynamic block:";
  const rows: string[] = [header];
  for (const line of lines) {
    if (line.kind !== "param") continue;
    const valuePart = formatValue(line.value);
    const typePart = `[${line.type}]`;
    const extras: string[] = [];
    if (line.defaultValue !== undefined) {
      extras.push(`default ${formatValue(line.defaultValue)}`);
    }
    if (line.min !== undefined) extras.push(`min ${line.min}`);
    if (line.max !== undefined) extras.push(`max ${line.max}`);
    if (line.choices && line.choices.length > 0) {
      extras.push(`choices: ${line.choices.join(", ")}`);
    }
    const suffix = extras.length > 0 ? ` (${extras.join(", ")})` : "";
    const namePad = line.name.length >= 12 ? line.name : line.name.padEnd(12);
    const typePad = typePart.length >= 12 ? typePart : typePart.padEnd(12);
    rows.push(`  ${namePad}${typePad}= ${valuePart}${suffix}`);
  }
  return rows.join("\n");
}

function formatValue(value: string | number | boolean): string {
  if (typeof value === "string") return value;
  return String(value);
}

// ═══════════════════════════════════════════════════════════════════════════
// DYNSET
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Parse a raw string value against the current primitive type. Returns
 * a parsed value on success or an error message on failure.
 *
 * Numeric values are routed through the unit-aware parsers in
 * `parseParameterValue.ts` so users can type `"12м"`, `"12cm"`, or
 * `"π/2"` and the value is converted to the drawing baseline (mm for
 * distances, degrees for angles) — matches AutoCAD Properties palette
 * behaviour.
 */
function parseValueForType(
  paramName: string,
  rawValue: string,
  currentValue: string | number | boolean,
  info: ParamInfo | undefined,
): { ok: true; value: string | number | boolean } | { ok: false; message: string } {
  if (typeof currentValue === "number") {
    // Distinguish angle from distance when the typed metadata is
    // available; fall back to the plain linear parser otherwise (which
    // also accepts bare numbers — safe baseline for suffixed keys like
    // `pt.x` whose typed info is stored on the parent).
    const kind = info?.type === "angle" ? "rotation" : "linear";
    const parsed = parseParameterValue(rawValue, kind);
    if (parsed === null) {
      return {
        ok: false,
        message:
          kind === "rotation"
            ? `DYNSET: '${rawValue}' is not a valid angle for ${paramName}. Use degrees (e.g. 90, 90°, 1.5rad, π/2).`
            : `DYNSET: '${rawValue}' is not a valid length for ${paramName}. Use mm, cm, m, in, ft (e.g. 12м, 12mm, 0.5m).`,
      };
    }
    // Range clamp: reject values that fall outside [min, max] when the
    // typed metadata provides them. Matches AutoCAD's Properties palette
    // which refuses out-of-range entries with a popup.
    if (info?.min !== undefined && parsed < info.min) {
      return {
        ok: false,
        message: `DYNSET: ${paramName} value ${parsed} below minimum ${info.min}.`,
      };
    }
    if (info?.max !== undefined && parsed > info.max) {
      return {
        ok: false,
        message: `DYNSET: ${paramName} value ${parsed} above maximum ${info.max}.`,
      };
    }
    return { ok: true, value: parsed };
  }
  if (typeof currentValue === "boolean") {
    const norm = rawValue.trim().toLowerCase();
    if (norm === "true" || norm === "1" || norm === "yes") {
      return { ok: true, value: true };
    }
    if (norm === "false" || norm === "0" || norm === "no") {
      return { ok: true, value: false };
    }
    return {
      ok: false,
      message: `DYNSET: '${rawValue}' is not a boolean for ${paramName}. Use true/false/yes/no/1/0.`,
    };
  }
  // string — apply choices validation if the block's typed metadata
  // supplies a choice list (visibility / lookup).
  if (info?.choices && info.choices.length > 0) {
    if (!info.choices.includes(rawValue)) {
      return {
        ok: false,
        message: `DYNSET: '${rawValue}' not in allowed values for ${paramName}. Choices: ${info.choices.join(", ")}`,
      };
    }
  }
  return { ok: true, value: rawValue };
}

/**
 * Compound parameter kinds addressable by a single DYNSET name. When
 * the user types `DYNSET pt=100,200` we split the value into
 * `<name>.x`/`<name>.y` (point / xy / basepoint) or
 * `<name>.distance`/`<name>.angle` (polar). Mirrors the
 * `applyInstanceParameters` suffix scheme in
 * drawing-model/dynamicBlockMetadata.ts.
 */
type CompoundKind = "polar" | "xy" | "point" | "basepoint";

function compoundKindFromParam(
  param: DynamicParameter,
): CompoundKind | undefined {
  if (param.kind === "polar") return "polar";
  if (param.kind === "xy") return "xy";
  if (param.kind === "point") return "point";
  if (param.kind === "basepoint") return "basepoint";
  return undefined;
}

function findCompoundParam(
  block: BlockDefinition | undefined,
  name: string,
): { kind: CompoundKind } | undefined {
  const params = block?.dynamicMetadata?.parameters ?? [];
  for (const p of params) {
    if (p.name !== name) continue;
    const kind = compoundKindFromParam(p);
    if (kind) return { kind };
  }
  return undefined;
}

/**
 * Update one (or two, for compound kinds) customProperty on the given
 * INSERT. Returns a new entity on success (input is not mutated) or a
 * typed error message.
 *
 * Compound names (`DYNSET pt=100,200` for a point / xy / polar
 * parameter) are split into the `<name>.x`/`<name>.y` or
 * `<name>.distance`/`<name>.angle` suffixed keys that
 * `applyInstanceParameters` consumes.
 */
export function setDynamicParameter(
  entity: DrawingEntity | undefined,
  block: BlockDefinition | undefined,
  paramName: string,
  rawValue: string,
): DynSetResult {
  const insert = asBlockInsert(entity);
  if (!insert) {
    return {
      ok: false,
      message: `DYNSET requires a Block entity; selected ${
        entity?.type ?? "none"
      }.`,
    };
  }
  const name = paramName.trim();
  if (!name) {
    return { ok: false, message: "DYNSET: parameter name is required." };
  }
  const custom = insert.customProperties ?? {};
  const keys = Object.keys(custom);
  if (keys.length === 0) {
    return {
      ok: false,
      message: "DYNSET: block has no dynamic parameters.",
    };
  }
  // Compound parameter branch — `pt=100,200` → { pt.x:100, pt.y:200 }.
  const compound = findCompoundParam(block, name);
  if (compound && !Object.prototype.hasOwnProperty.call(custom, name)) {
    return applyCompoundDynset(insert, custom, name, compound.kind, rawValue);
  }
  if (!Object.prototype.hasOwnProperty.call(custom, name)) {
    return {
      ok: false,
      message: `DYNSET: unknown parameter ${name}. Available: ${keys.join(", ")}`,
    };
  }
  const before = custom[name]!;
  const infoMap = buildParamInfoMap(block);
  const info = infoMap.get(name);
  const parsed = parseValueForType(name, rawValue, before, info);
  if (!parsed.ok) return parsed;
  const nextCustom: Record<string, string | number | boolean> = {
    ...custom,
    [name]: parsed.value,
  };
  const nextEntity: BlockInsertEntity = {
    ...insert,
    customProperties: nextCustom,
  };
  return {
    ok: true,
    entity: nextEntity,
    before,
    after: parsed.value,
    summary: `DYNSET ok: ${name} ${formatValue(before)} → ${formatValue(parsed.value)}`,
  };
}

/**
 * Apply a compound `DYNSET name=a,b` value — splits the pair and
 * writes both suffixed keys in one pass. Point / xy / basepoint use
 * `.x`/`.y`, polar uses `.distance`/`.angle` (angle in degrees). Every
 * coordinate goes through the unit-aware parser so `"1m,50cm"` works.
 */
function applyCompoundDynset(
  insert: BlockInsertEntity,
  custom: Record<string, string | number | boolean>,
  base: string,
  kind: CompoundKind,
  rawValue: string,
): DynSetResult {
  if (kind === "polar") {
    // Polar: distance + angle, comma-separated. Parse distance via the
    // linear parser and angle via the angle parser.
    const idx = rawValue.indexOf(",");
    if (idx < 0) {
      return {
        ok: false,
        message: `DYNSET: polar parameter ${base} requires 'distance,angle' value.`,
      };
    }
    const distRaw = rawValue.slice(0, idx).trim();
    const angRaw = rawValue.slice(idx + 1).trim();
    const dist = parseParameterValue(distRaw, "linear");
    const ang = parseAngleValue(angRaw);
    if (dist === null) {
      return {
        ok: false,
        message: `DYNSET: '${distRaw}' is not a valid distance for ${base}.`,
      };
    }
    if (ang === null) {
      return {
        ok: false,
        message: `DYNSET: '${angRaw}' is not a valid angle for ${base}.`,
      };
    }
    const keyDist = `${base}.distance`;
    const keyAng = `${base}.angle`;
    const beforeDist = custom[keyDist] ?? 0;
    const beforeAng = custom[keyAng] ?? 0;
    const nextCustom: Record<string, string | number | boolean> = {
      ...custom,
      [keyDist]: dist,
      [keyAng]: ang,
    };
    const nextEntity: BlockInsertEntity = {
      ...insert,
      customProperties: nextCustom,
    };
    return {
      ok: true,
      entity: nextEntity,
      before: `${formatValue(beforeDist)},${formatValue(beforeAng)}`,
      after: `${dist},${ang}`,
      summary: `DYNSET ok: ${base} ${formatValue(beforeDist)},${formatValue(beforeAng)} → ${dist},${ang}`,
    };
  }
  // point / xy / basepoint — (x, y) pair in drawing units.
  const pt = parsePointValue(rawValue);
  if (pt === null) {
    return {
      ok: false,
      message: `DYNSET: '${rawValue}' is not a valid point for ${base}. Use 'x,y' or 'x y'.`,
    };
  }
  const keyX = `${base}.x`;
  const keyY = `${base}.y`;
  const beforeX = custom[keyX] ?? 0;
  const beforeY = custom[keyY] ?? 0;
  const nextCustom: Record<string, string | number | boolean> = {
    ...custom,
    [keyX]: pt.x,
    [keyY]: pt.y,
  };
  const nextEntity: BlockInsertEntity = {
    ...insert,
    customProperties: nextCustom,
  };
  return {
    ok: true,
    entity: nextEntity,
    before: `${formatValue(beforeX)},${formatValue(beforeY)}`,
    after: `${pt.x},${pt.y}`,
    summary: `DYNSET ok: ${base} ${formatValue(beforeX)},${formatValue(beforeY)} → ${pt.x},${pt.y}`,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// DYNRESET
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Replace the INSERT's customProperties with the master block's
 * authored defaults. Keys NOT covered by the typed metadata are
 * dropped — AutoCAD's reset removes every per-INSERT override, so the
 * bag collapses back to exactly the master defaults.
 *
 * When the block has no typed dynamicMetadata (real AutoCAD output
 * that only set `dynamicBlock: true`) we cannot derive defaults from
 * inside the reducer — emit a warning and leave the entity unchanged.
 */
export function resetDynamicParameters(
  entity: DrawingEntity | undefined,
  block: BlockDefinition | undefined,
): DynResetResult {
  const insert = asBlockInsert(entity);
  if (!insert) {
    return {
      ok: false,
      message: `DYNRESET requires a Block entity; selected ${
        entity?.type ?? "none"
      }.`,
    };
  }
  if (!block) {
    return {
      ok: false,
      message: `DYNRESET: block definition for ${insert.blockName} not found.`,
    };
  }
  const infoMap = buildParamInfoMap(block);
  if (infoMap.size === 0) {
    // No typed metadata. If customProperties is already empty we treat
    // this as a no-op success (nothing to reset); otherwise flag the
    // caller so the UI surfaces the limitation.
    const currentKeys = Object.keys(insert.customProperties ?? {});
    if (currentKeys.length === 0) {
      return {
        ok: true,
        entity: insert,
        count: 0,
        summary: "DYNRESET ok: restored 0 parameters from master.",
      };
    }
    return {
      ok: false,
      message: `DYNRESET: block ${insert.blockName} has no typed parameter metadata to reset from.`,
    };
  }
  const nextCustom: Record<string, string | number | boolean> = {};
  for (const info of infoMap.values()) {
    nextCustom[info.name] = info.defaultValue;
  }
  const nextEntity: BlockInsertEntity = {
    ...insert,
    customProperties: nextCustom,
  };
  return {
    ok: true,
    entity: nextEntity,
    count: infoMap.size,
    summary: `DYNRESET ok: restored ${infoMap.size} parameter(s) from master.`,
  };
}
